import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationModalComponent } from '@core-lib/components/confirmation-modal/confirmation-modal.component';
import { AppConstants } from '@core-lib/config/app.constants';
import { ErrorCode } from '@core-lib/enums';
import { ActionType } from '@core-lib/enums/action-type.enum';
import { AppLanguage } from '@core-lib/enums/app-language.enum';
import { IAction } from '@core-lib/models/action.model';
import { DocumentGedApiService } from '@core-lib/modules/document-ged/services/document-ged-api.service';
import { CompteBancaireService } from '@core-lib/services/compte-bancaire.service';
import { ConfigService } from '@core-lib/services/config.service';
import { ReferentialsService } from '@core-lib/services/referentials.service';
import * as featureActions from '@core-lib/store/shared-store/actions';
import { LoadTypesDocumentsRequest, Noop } from '@core-lib/store/shared-store/actions';
import * as featureSelectors from '@core-lib/store/shared-store/selectors';
import { NgSelectConfig } from '@ng-select/ng-select';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ngSelectConfigI18n } from '@shared-assets/i18n/ng-select';
import { DateAdapter } from '@angular/material/core';
import { ModalRef, ModalService } from '@core-lib/modules/modal-container';
import { of } from 'rxjs';
import { catchError, debounceTime, delay, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class SharedStoreEffects {
	defaultSelectConfig: NgSelectConfig;

	constructor(
		private actions$: Actions,
		private store$: Store,
		private translateService: TranslateService,
		private dateAdapter: DateAdapter<any>,
		private refentialsService: ReferentialsService,
		private modalService: ModalService,
		private configService: ConfigService,
		private ngSelectConfig: NgSelectConfig,
		private router: Router,
		private documentGedApiService: DocumentGedApiService,
		private compteBancaireService: CompteBancaireService
	) {
		ngSelectConfig.clearSearchOnAdd = true;
		this.defaultSelectConfig = { ...ngSelectConfig };
	}

	loadConfigRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.LoadConfigRequest),
			switchMap(_ =>
				this.configService.get().pipe(
					map(config => {
						localStorage.setItem(AppConstants.LOCALSTORAGE_KEYS.deviseReference, JSON.stringify(config.deviseReference))
						return featureActions.LoadConfigSuccess({ entity: config });
					}),
					catchError(error => of(featureActions.LoadConfigFailure({ error })))
				)
			)
		)
	);

	initLanguages$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(featureActions.InitLanguages),
				withLatestFrom(this.store$.select(featureSelectors.selectState)),
				tap(([action, store]) => {
					const currentLanguage = store.language;
					this.translateService.addLangs(Object.values(AppLanguage));
					this.translateService.setDefaultLang(currentLanguage);
					this.translateService.use(currentLanguage);

					this.dateAdapter.setLocale(currentLanguage);

					this.ngSelectConfig = {
						...this.defaultSelectConfig,
						...ngSelectConfigI18n[currentLanguage]
					};
				})
			),
		{ dispatch: false }
	);

	setLanguage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(featureActions.SetLanguage),
				map(action => {
					this.translateService.use(action.param);
					this.dateAdapter.setLocale(action.param);

					this.ngSelectConfig = {
						...this.defaultSelectConfig,
						...ngSelectConfigI18n[action.param]
					};
				})
			),
		{ dispatch: false }
	);

	loadReferentialsRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.LoadReferentialsRequest),
			switchMap(action =>
				this.refentialsService.get(action.entity).pipe(
					map(referentials =>
						featureActions.LoadReferentialsSuccess({
							entity: referentials
						})
					),
					catchError(error => of(featureActions.LoadReferentialsFailure({ error })))
				)
			)
		)
	);

	navigate$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(featureActions.Navigate),
				tap(action => {
					this.router.navigate(action.param, action.extras ?? {});
				})
			),
		{ dispatch: false }
	);

	openConfirmationModal$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.OpenConfirmationModal),
			map(action => {
				const modalRef: ModalRef = this.modalService.show(ConfirmationModalComponent, {
					...action.modalOptions,
					ignoreBackdropClick: true
				});
				const modal: ConfirmationModalComponent = modalRef.content;

				modal.config = action.config;

				modal.action.pipe(takeUntil(modal.destroySubscriptions$)).subscribe((result: IAction) => {
					this.store$.dispatch(
						featureActions.CloseConfirmationModal({
							actionType: result.type,
							correlationParams: action.correlationParams
						})
					);

					if (result.type === ActionType.Close && action.cancelAction) {
						[].concat(action.cancelAction).forEach(a => this.store$.dispatch(a));
					}

					if (result.type === ActionType.Confirm && action.confirmationAction) {
						[].concat(action.confirmationAction).forEach(a => this.store$.dispatch(a));
					}
				});

				return featureActions.SetConfirmationModal({
					param: modalRef.id
				});
			})
		)
	);

	openCancelConfirmationModal$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.OpenCancelConfirmationModal),
			map(action => {
				const modalRef: ModalRef = this.modalService.show(ConfirmationModalComponent, {
					...action.modalOptions,
					ignoreBackdropClick: true
				});
				const modal: ConfirmationModalComponent = modalRef.content;

				modal.config = action.config;

				modal.action.pipe(takeUntil(modal.destroySubscriptions$)).subscribe((result: IAction) => {
					if (result.type === ActionType.Close) {
						this.store$.dispatch(
							featureActions.CloseCancelConfirmationModal({
								actionType: result.type,
								correlationParams: action.correlationParams
							})
						);
					}

					if (result.type === ActionType.Confirm) {
						this.store$.dispatch(
							featureActions.CloseCancelConfirmationModal({
								actionType: result.type,
								nextAction: action.confirmationAction,
								correlationParams: action.correlationParams
							})
						);
					}
				});

				return featureActions.SetCancelConfirmationModal({
					param: modalRef.id
				});
			})
		)
	);

	closeConfirmationModal$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.CloseConfirmationModal),
			withLatestFrom(this.store$.select(featureSelectors.selectState)),
			debounceTime(200),
			map(([action, state]) => {
				this.modalService.hide(state.confirmModal);
				return featureActions.SetConfirmationModal({ param: null });
			})
		)
	);

	closeCancelConfirmationModal$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.CloseCancelConfirmationModal),
			withLatestFrom(this.store$.select(featureSelectors.selectState)),
			debounceTime(200),
			switchMap(([action, state]) => {
				this.modalService.hide(state.cancelConfirmModal);
				if (action.nextAction) {
					return [featureActions.SetConfirmationModal({ param: null }), ...[].concat(action.nextAction)];
				}
				return [featureActions.SetConfirmationModal({ param: null })];
			})
		)
	);

	addToControlAutocomplete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.AddToControlAutocomplete),
			delay(200),
			map(action => featureActions.AddToControlAutocompleteSuccess({ param: action.param }))
		)
	);

	loadTypesDocumentsRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LoadTypesDocumentsRequest),
			withLatestFrom(this.store$.select(featureSelectors.selectTypesDocuments)),
			switchMap(([action, types]) =>
				this.documentGedApiService.loadTypesPieceJointe().pipe(
					switchMap(typesdocuments => [featureActions.LoadTypesDocumentsSuccess({ entity: typesdocuments })]),
					catchError(error => of(featureActions.LoadTypesDocumentsFailure({ error })))
				)
			)
		)
	);

	BalanceEcarts$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.BalanceEcarts),
			map(action => {
				this.compteBancaireService.balanceEcarts.next(true);
				return Noop();
			})
		)
	);

	// Error handling
	handleErrorExceptionGlobal$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(featureActions.HandleErrorException),
			switchMap(action => {
				let errorCode = (<any>Object).values(ErrorCode).includes(action.errorCode) ? action.errorCode : "NotDefinedError";
				
				let actions = [
					featureActions.OpenConfirmationModal({
						textsKey: "Errors." + errorCode,
						titleParams: { action: action.typeAction ?? '' },
						bodyParams: { error : action.error},
						confirmBtnText: 'Actions.Close',
						canClose: false
					}),
					action.cancelAction ? action.cancelAction : featureActions.Noop()
				];
				actions = actions.concat(action.failureAction ? action.failureAction : [featureActions.Noop()]);
				return actions;
			})
		);
	});
	
}
